import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Lightbulb, Loader, CheckCircle, AlertCircle, TrendingUp, Target, Shield, Star, Zap, Gift } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

export default function OfferAnalyzer({ onAnalysisComplete }) {
  const { getToken } = useAuth();
  
  // Form state
  const [businessDescription, setBusinessDescription] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const [industry, setIndustry] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [step, setStep] = useState('input'); // 'input', 'analyzing', 'results'
  
  const handleAnalyze = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    setStep('analyzing');
    
    try {
      const token = await getToken();
      
      const response = await fetch(`${API_URL}/api/offer/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          businessDescription,
          landingPageUrl,
          industry,
          targetAudience
        })
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Analysis failed');
      }
      
      setAnalysis(data.angles);
      setStep('results');
      
      // Notify parent component
      if (onAnalysisComplete) {
        onAnalysisComplete({
          angles: data.angles,
          input: { businessDescription, landingPageUrl, industry, targetAudience }
        });
      }
      
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.message);
      setStep('input');
    } finally {
      setLoading(false);
    }
  };
  
  const getAngleIcon = (angleType) => {
    const icons = {
      problem_pain: AlertCircle,
      value_proposition: Target,
      usps: Zap,
      value_boosters: Gift,
      social_proof: Star,
      risk_removal: Shield
    };
    return icons[angleType] || Lightbulb;
  };
  
  const getAngleColor = (angleType) => {
    const colors = {
      problem_pain: '#ef4444',
      value_proposition: '#3b82f6',
      usps: '#f59e0b',
      value_boosters: '#10b981',
      social_proof: '#8b5cf6',
      risk_removal: '#06b6d4'
    };
    return colors[angleType] || '#6b7280';
  };
  
  const getAngleTitle = (angleType) => {
    const titles = {
      problem_pain: 'Problem/Pain Points',
      value_proposition: 'Value Proposition',
      usps: 'Unique Selling Points',
      value_boosters: 'Value Boosters',
      social_proof: 'Social Proof & Authority',
      risk_removal: 'Risk Removal'
    };
    return titles[angleType] || angleType;
  };
  
  return (
    <div style={{
      maxWidth: 1200,
      margin: '0 auto',
      padding: 20
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        borderRadius: 16,
        padding: 32,
        marginBottom: 32,
        color: 'white'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <Lightbulb size={32} />
          <h1 style={{ margin: 0, fontSize: 32, fontWeight: 700 }}>Offer Intelligence Engine</h1>
        </div>
        <p style={{ margin: 0, fontSize: 16, opacity: 0.9 }}>
          AI-powered analysis of your business to extract 6 key message angles for compelling ad copy
        </p>
      </div>
      
      {/* Input Form */}
      {step === 'input' && (
        <div style={{
          background: '#1f2937',
          borderRadius: 16,
          padding: 32
        }}>
          <h2 style={{ color: 'white', fontSize: 24, marginBottom: 24 }}>Tell us about your business</h2>
          
          <form onSubmit={handleAnalyze}>
            {/* Business Description */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block',
                color: '#9ca3af',
                fontSize: 14,
                fontWeight: 500,
                marginBottom: 8
              }}>
                Business Description *
              </label>
              <textarea
                value={businessDescription}
                onChange={(e) => setBusinessDescription(e.target.value)}
                required
                placeholder="Describe your business, what you offer, and what makes you unique..."
                rows={5}
                style={{
                  width: '100%',
                  padding: 12,
                  background: '#111827',
                  border: '1px solid #374151',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14,
                  resize: 'vertical'
                }}
              />
              <p style={{ fontSize: 12, color: '#6b7280', margin: '8px 0 0 0' }}>
                Be specific about your products/services, target market, and key benefits
              </p>
            </div>
            
            {/* Landing Page URL */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block',
                color: '#9ca3af',
                fontSize: 14,
                fontWeight: 500,
                marginBottom: 8
              }}>
                Landing Page URL (optional)
              </label>
              <input
                type="url"
                value={landingPageUrl}
                onChange={(e) => setLandingPageUrl(e.target.value)}
                placeholder="https://yourbusiness.com"
                style={{
                  width: '100%',
                  padding: 12,
                  background: '#111827',
                  border: '1px solid #374151',
                  borderRadius: 8,
                  color: 'white',
                  fontSize: 14
                }}
              />
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              {/* Industry */}
              <div>
                <label style={{
                  display: 'block',
                  color: '#9ca3af',
                  fontSize: 14,
                  fontWeight: 500,
                  marginBottom: 8
                }}>
                  Industry
                </label>
                <input
                  type="text"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  placeholder="e.g., SaaS, E-commerce, Services"
                  style={{
                    width: '100%',
                    padding: 12,
                    background: '#111827',
                    border: '1px solid #374151',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14
                  }}
                />
              </div>
              
              {/* Target Audience */}
              <div>
                <label style={{
                  display: 'block',
                  color: '#9ca3af',
                  fontSize: 14,
                  fontWeight: 500,
                  marginBottom: 8
                }}>
                  Target Audience
                </label>
                <input
                  type="text"
                  value={targetAudience}
                  onChange={(e) => setTargetAudience(e.target.value)}
                  placeholder="e.g., B2B SaaS companies"
                  style={{
                    width: '100%',
                    padding: 12,
                    background: '#111827',
                    border: '1px solid #374151',
                    borderRadius: 8,
                    color: 'white',
                    fontSize: 14
                  }}
                />
              </div>
            </div>
            
            {error && (
              <div style={{
                marginTop: 24,
                padding: 16,
                background: '#7f1d1d',
                border: '1px solid #dc2626',
                borderRadius: 8,
                color: '#fca5a5'
              }}>
                {error}
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || !businessDescription}
              style={{
                marginTop: 32,
                width: '100%',
                padding: 16,
                background: loading ? '#4b5563' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontSize: 16,
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8
              }}
            >
              {loading ? (
                <>
                  <Loader size={20} style={{ animation: 'spin 1s linear infinite' }} />
                  Analyzing...
                </>
              ) : (
                <>
                  <TrendingUp size={20} />
                  Analyze My Offer
                </>
              )}
            </button>
          </form>
        </div>
      )}
      
      {/* Analyzing State */}
      {step === 'analyzing' && (
        <div style={{
          background: '#1f2937',
          borderRadius: 16,
          padding: 64,
          textAlign: 'center'
        }}>
          <Loader size={64} color="#667eea" style={{ animation: 'spin 1s linear infinite', margin: '0 auto 24px' }} />
          <h2 style={{ color: 'white', fontSize: 24, marginBottom: 12 }}>Analyzing Your Offer...</h2>
          <p style={{ color: '#9ca3af', fontSize: 16 }}>
            Our AI is extracting 6 key message angles from your business context
          </p>
        </div>
      )}
      
      {/* Results */}
      {step === 'results' && analysis && (
        <div>
          <div style={{
            background: '#065f46',
            border: '1px solid #10b981',
            borderRadius: 12,
            padding: 16,
            marginBottom: 32,
            display: 'flex',
            alignItems: 'center',
            gap: 12
          }}>
            <CheckCircle size={24} color="#10b981" />
            <div>
              <h3 style={{ color: '#10b981', fontSize: 16, fontWeight: 600, margin: 0 }}>
                Analysis Complete!
              </h3>
              <p style={{ color: '#d1fae5', fontSize: 14, margin: 0 }}>
                6 message angles extracted. Review and use them to generate high-converting ad copy.
              </p>
            </div>
          </div>
          
          {/* Angle Cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            {Object.entries(analysis).map(([angleType, angleData]) => {
              const Icon = getAngleIcon(angleType);
              const color = getAngleColor(angleType);
              
              return (
                <div
                  key={angleType}
                  style={{
                    background: '#1f2937',
                    border: `2px solid ${color}20`,
                    borderRadius: 16,
                    padding: 24,
                    transition: 'all 0.2s',
                    cursor: 'default'
                  }}
                >
                  {/* Header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      background: `${color}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon size={20} color={color} />
                    </div>
                    <h3 style={{ color: 'white', fontSize: 18, fontWeight: 600, margin: 0 }}>
                      {getAngleTitle(angleType)}
                    </h3>
                  </div>
                  
                  {/* Content */}
                  <div style={{ color: '#9ca3af', fontSize: 14, lineHeight: 1.6 }}>
                    {Object.entries(angleData).map(([key, value]) => {
                      if (key === 'headline_phrases') {
                        return (
                          <div key={key} style={{ marginTop: 16 }}>
                            <div style={{ fontWeight: 600, color: '#d1d5db', marginBottom: 8 }}>
                              Headline Phrases:
                            </div>
                            {value.map((phrase, i) => (
                              <div
                                key={i}
                                style={{
                                  background: '#111827',
                                  padding: 8,
                                  borderRadius: 6,
                                  marginBottom: 4,
                                  fontSize: 13,
                                  color: color,
                                  fontWeight: 500
                                }}
                              >
                                "{phrase}"
                              </div>
                            ))}
                          </div>
                        );
                      }
                      
                      if (!value || value === '...' || value === 'Not specified') return null;
                      
                      return (
                        <div key={key} style={{ marginBottom: 8 }}>
                          <strong style={{ color: '#d1d5db' }}>
                            {key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}:
                          </strong>{' '}
                          {value}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Actions */}
          <div style={{
            marginTop: 32,
            display: 'flex',
            gap: 16,
            justifyContent: 'center'
          }}>
            <button
              onClick={() => setStep('input')}
              style={{
                padding: '12px 24px',
                background: '#374151',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Analyze Another Offer
            </button>
            
            <button
              onClick={() => {
                if (onAnalysisComplete) {
                  onAnalysisComplete({
                    angles: analysis,
                    input: { businessDescription, landingPageUrl, industry, targetAudience },
                    action: 'use-in-campaign'
                  });
                }
              }}
              style={{
                padding: '12px 24px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: 8,
                color: 'white',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Use in Campaign Builder
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Add keyframe animation for spinner
const style = document.createElement('style');
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(style);
